import { Controller, RegisterOptions, useFormContext } from "react-hook-form";

import ComboBox, { ComboBoxProps } from "@/components/molecules/combobox/ComboBox";
import FormControl, { FormControlProps } from "@/components/molecules/form/FormControl";

export type FormInputProps<T extends { id: string; name: string }, Multiple extends boolean = false> = Omit<
  ComboBoxProps<T, Multiple>,
  "value" | "onSelect"
> &
  Omit<FormControlProps, "id" | "children"> & {
    name: string;
    rules?: RegisterOptions;
    disabled?: boolean;
    multiple?: Multiple;
    onTyping?: (query: string) => void;
  };

const FormComboBox = <T extends { id: string; name: string }, Multiple extends boolean = false>({
  name,
  rules,
  disabled = false,
  multiple,
  onTyping,
  ...comboBoxProps
}: FormInputProps<T, Multiple>) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, name, value }, fieldState: { error } }) => {
        return (
          <FormControl id={name} error={error?.message} {...comboBoxProps}>
            <ComboBox
              {...comboBoxProps}
              id={name}
              multiple={multiple ?? false}
              onSelect={onChange}
              value={value}
              hasError={!!error}
              disabled={disabled}
              onTyping={onTyping}
            />
          </FormControl>
        );
      }}
    />
  );
};

export default FormComboBox;
